import { useEffect, useState } from "react";

export type UseCountUpProps = {
  to: number;
  from: number;
  duration: number;
  delay?: number;
};

export function useCountUp({ to, from = 0, duration = 1000, delay = 0 }: UseCountUpProps): number {
  const [value, setValue] = useState(from);

  useEffect(() => {
    if (from === to || duration <= 0) {
      setValue(to);
      return;
    }

    let cleanup: (() => void) | undefined;
    const timeoutId = setTimeout(() => {
      const start = performance.now();
      let animationFrameId: number;

      function tick(now: number) {
        const elapsed = now - start;
        const progress = Math.min(elapsed / duration, 1);
        const current = Math.round(from + (to - from) * progress);
        setValue(current);

        if (progress < 1) {
          animationFrameId = requestAnimationFrame(tick);
        }
      }

      animationFrameId = requestAnimationFrame(tick);

      cleanup = () => {
        cancelAnimationFrame(animationFrameId);
      };
    }, delay);

    return () => {
      clearTimeout(timeoutId);
      cleanup?.();
    };
  }, [to, from, duration, delay]);

  return value;
}
